import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '@/trpc/client';
import { IArticleCardProps } from '@/components/ui/ArticleCard';
import { ArticleLink } from '@/apiTypes/types';
import { listQueryOptions } from '@/lib/queries/articleQueries';

const DEFAULT_LIMIT = 19;

const mapArticleLinkToCard = (articles: ArticleLink[]): IArticleCardProps[] =>
  articles.map((article) => ({
    id: article.id,
    url: article.permalink,
    headline: article.headlinePlain,
    image: article.image,
  }));

const useBlogPageArticles = (
  initialPagination: {
    total: number;
    limit: number;
    from: number;
  } = {
    total: 0,
    limit: DEFAULT_LIMIT,
    from: 0,
  },
) => {
  const trpc = useTRPC();
  const [page, setPage] = useState({ from: initialPagination.from, limit: initialPagination.limit });

  const query = useQuery(listQueryOptions({ trpc, from: page.from, limit: page.limit }));

  const currentItems = useMemo(() => (query.data ? mapArticleLinkToCard(query.data.articles) : []), [query.data]);

  const currentPagination = query.data?.pagination ?? {
    total: initialPagination.total,
    limit: page.limit,
    from: page.from,
    isEnd: true,
  };

  const loadMore = useCallback(async (from: number, limit: number) => {
    setPage({ from, limit });
  }, []);

  return {
    currentItems,
    currentPagination,
    loadMore,
    error: query.error,
    isError: query.isError,
  };
};

export default useBlogPageArticles;
